import { useRef, useState } from "react";
import { FolderUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, errMsg } from "@/lib/api";

const OK_EXT = ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx", "png", "jpg", "jpeg", "webp", "txt", "csv"];
const MAX = 50 * 1024 * 1024;

const ext = (n) => (n.includes(".") ? n.split(".").pop().toLowerCase() : "");
export const titleFrom = (n) => n.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
export const categoryFrom = (path) => {
  const parts = (path || "").split("/").filter(Boolean);
  return parts.length > 2 ? parts[parts.length - 2] : "General";
};

export const BulkUpload = ({ onDone, audience = "shared" }) => {
  const folderRef = useRef();
  const filesRef = useRef();
  const [prog, setProg] = useState(null);

  const run = async (list) => {
    const files = Array.from(list || []).filter((f) => !f.name.startsWith(".") && OK_EXT.includes(ext(f.name)));
    const skipped = (list?.length || 0) - files.length;
    if (!files.length) { toast.error("No supported files found (PDF, Word, PowerPoint, Excel, images, TXT)."); return; }
    let ok = 0;
    const failed = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProg({ done: i, total: files.length, name: f.name });
      if (f.size > MAX) { failed.push(`${f.name} (over 50MB)`); continue; }
      const fd = new FormData();
      fd.append("title", titleFrom(f.name));
      fd.append("category", categoryFrom(f.webkitRelativePath));
      fd.append("type", "template");
      fd.append("audience", audience);
      fd.append("file", f);
      try { await api.post("/admin/resources", fd); ok++; } catch (e) { failed.push(`${f.name} (${errMsg(e)})`); }
    }
    setProg(null);
    folderRef.current.value = "";
    filesRef.current.value = "";
    toast.success(`Uploaded ${ok} template${ok === 1 ? "" : "s"}${skipped ? ` · skipped ${skipped} unsupported file${skipped === 1 ? "" : "s"}` : ""}`);
    if (failed.length) toast.error(`Couldn't upload: ${failed.join(", ")}`);
    onDone();
  };

  return (
    <div className="rounded-2xl border-2 border-dashed border-pinkb/40 bg-card p-6 mb-6 max-w-3xl" data-testid="bulk-upload">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="max-w-md">
          <div className="font-medium">Bulk upload templates</div>
          <p className="text-sm text-muted-foreground mt-1">Pick a folder of PDFs, Word, Canva exports or images. Each file becomes a downloadable template named from its file name, and each sub-folder becomes a category.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => folderRef.current.click()} disabled={!!prog} data-testid="bulk-upload-folder-btn" className="rounded-full bg-pinkb hover:bg-pinkb/90 text-white">
            {prog ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderUp className="h-4 w-4 mr-2" />}Choose folder
          </Button>
          <Button onClick={() => filesRef.current.click()} disabled={!!prog} variant="outline" data-testid="bulk-upload-files-btn" className="rounded-full">Choose files</Button>
        </div>
      </div>
      <input ref={folderRef} type="file" hidden multiple webkitdirectory="" directory="" data-testid="bulk-upload-folder-input" onChange={(e) => run(e.target.files)} />
      <input ref={filesRef} type="file" hidden multiple accept={OK_EXT.map((x) => `.${x}`).join(",")} data-testid="bulk-upload-files-input" onChange={(e) => run(e.target.files)} />
      {prog && (
        <div className="mt-4" data-testid="bulk-upload-progress">
          <Progress value={(prog.done * 100) / prog.total} className="h-2" />
          <p className="text-xs text-muted-foreground mt-2 truncate">Uploading {prog.done + 1} of {prog.total}: {prog.name}</p>
        </div>
      )}
    </div>
  );
};
